// PATH: src/pages/QuestReviewPage.tsx
import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAltRounded";
import { useQuestReviewApi } from "../api";
import {
  learnErrorMessage,
  type QueueEntry,
  type QueueResponse,
  type QuestBody,
} from "../api/questReview/questReview";
import { EmptyState, PageHeader, StatusPill, type PillTone, useConfirm, useToast } from "../components/ui";
import QuestView from "../components/questReview/QuestView";
import FactChain from "../components/questReview/FactChain";
import QuestEditor from "../components/questReview/QuestEditor";
import QualityByDocument from "../components/questReview/QualityByDocument";

/**
 * « Quêtes à corriger » (lot L6) : une quête partagée détectée fausse —
 * signalée par des élèves, retirée du partage par un relecteur, ou dont un
 * texte est jugé faux par la vérification des textes (lot L4 : elle reste
 * servie en attendant) — est tranchée ici par un humain (ADMIN ou REVIEWER). Valider la remet en service
 * telle quelle, Corriger sert la version réécrite à tous les élèves suivants,
 * Rejeter la retire pour de bon. Chaque décision clôt ses signalements.
 *
 * L'écran remonte la chaîne jusqu'au cours : la quête, puis les faits que la
 * génération a reçus, puis les passages du cours d'où ils viennent — pour
 * voir si l'erreur vient du cours, d'un fait ou du générateur.
 */

const REASONS: Record<string, string> = {
  exercise_wrong: "Exercice faux",
  narration_incoherent: "Narration incohérente",
  visual_wrong: "Illustration fausse",
  other: "Autre",
};

const VERDICTS: Record<string, string> = {
  body_text_suspect: "Texte jugé faux par la vérification des textes (quête toujours servie)",
  body_audit_rejected: "Rejetée par l'auditeur avant partage",
  body_audit_passed: "Acceptée par l'auditeur",
  body_suspended_review: "Jugée fausse par le relecteur après signalement",
  body_report_review_passed: "Jugée juste par le relecteur après signalement",
  body_suspended_reporters: "Suspendue : plusieurs élèves l'ont signalée",
};

/** Pourquoi l'entrée est dans la file, en un badge. */
const whyInQueue = (e: QueueEntry): { label: string; tone: PillTone } => {
  if (e.reporters > 0) return { label: `Signalée par ${e.reporters} élève${e.reporters > 1 ? "s" : ""}`, tone: "error" };
  if (e.disabled_reason === "audit") return { label: "Rejetée par l'audit", tone: "warning" };
  if (e.disabled_reason === "report_review") return { label: "Jugée fausse par le relecteur", tone: "warning" };
  if (e.disabled_reason === "reporters") return { label: "Suspendue par les signalements", tone: "warning" };
  if (e.human_queue_reason === "text_suspect") return { label: "Texte suspect, toujours servie", tone: "neutral" };
  return { label: "À trancher", tone: "neutral" };
};

const formOf = (e: QueueEntry): string => {
  const step = (e.body?.question_chain ?? [])[0];
  return step?.render_type ?? e.template_id ?? "—";
};

const day = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "");

const QuestReviewPage: React.FC = () => {
  const api = useQuestReviewApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = React.useState<"queue" | "documents">("queue");
  const [queue, setQueue] = React.useState<QueueResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.getQueue(50);
      setQueue(data);
      setSelectedId((prev) => (prev && data.entries.some((e) => e.id === prev) ? prev : data.entries[0]?.id ?? null));
    } catch (e) {
      setError(learnErrorMessage(e, "Impossible de charger la file."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const selected = queue?.entries.find((e) => e.id === selectedId) ?? null;

  const act = async (run: () => Promise<{ data: { closed_reports: number[] } }>, done: string) => {
    setBusy(true);
    try {
      const { data } = await run();
      const n = data.closed_reports.length;
      toast.success(n ? `${done} ${n} signalement${n > 1 ? "s" : ""} clos.` : done);
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(learnErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const validate = async (e: QueueEntry) => {
    const ok = await confirm({
      title: "Valider cette quête ?",
      message: "Elle est juste telle quelle : elle redevient servie aux élèves et ne sera plus revérifiée par un modèle.",
      confirmLabel: "Valider",
    });
    if (ok) await act(() => api.validateBody(e.id), "Quête validée.");
  };

  const reject = async (e: QueueEntry) => {
    const ok = await confirm({
      title: "Rejeter cette quête ?",
      message: "Elle ne sera plus jamais servie. Les élèves recevront une autre quête sur cet objectif.",
      confirmLabel: "Rejeter",
      danger: true,
    });
    if (ok) await act(() => api.rejectBody(e.id), "Quête rejetée.");
  };

  const correct = async (e: QueueEntry, body: QuestBody) => {
    setBusy(true);
    setEditError(null);
    try {
      const { data } = await api.correctBody(e.id, body);
      toast.success(`Correction enregistrée : elle est servie aux élèves suivants. ${data.closed_reports.length} signalement(s) clos.`);
      setEditing(false);
      await load();
    } catch (err) {
      // 422 : la structure a bougé, ou la quête n'est plus jouable — on le
      // dit dans le formulaire, sans perdre la saisie.
      setEditError(learnErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const close = (e: QueueEntry, resolution: "dismissed" | "fixed") =>
    act(() => api.closeQuest(e.id, resolution), resolution === "dismissed" ? "Signalement classé : juste." : "Signalement classé : faux.");

  return (
    <Box>
      <PageHeader
        title="Quêtes à corriger"
        subtitle="Les quêtes partagées que des élèves ont signalées ou qu'un relecteur a retirées : la quête, les faits qui l'ont produite et le cours d'où ils viennent, côte à côte."
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="queue" label={`File${queue ? ` (${queue.total})` : ""}`} />
          <Tab value="documents" label="Par chapitre" />
        </Tabs>
      </PageHeader>

      {tab === "documents" && <QualityByDocument />}

      {tab === "queue" && error && (
        <Alert severity="error" action={<Button onClick={() => void load()}>Réessayer</Button>}>
          {error}
        </Alert>
      )}
      {tab === "queue" && !error && !queue && <Skeleton variant="rounded" height={320} />}
      {tab === "queue" && queue && queue.entries.length === 0 && (
        <EmptyState
          icon={<TaskAltIcon />}
          title="Rien à corriger"
          description="Aucune quête signalée ni retirée du partage n'attend de décision."
        />
      )}

      {tab === "queue" && queue && queue.entries.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "340px 1fr" }, gap: 2, alignItems: "start" }}>
          <Card variant="outlined">
            <List dense disablePadding sx={{ maxHeight: { lg: "calc(100vh - 220px)" }, overflowY: "auto" }}>
              {queue.entries.map((e) => {
                const why = whyInQueue(e);
                return (
                  <ListItemButton key={e.id} selected={e.id === selectedId} onClick={() => setSelectedId(e.id)} divider>
                    <ListItemText
                      primary={
                        <Typography variant="body2" dir="auto" noWrap>
                          {e.facts.objective_title || e.body?.question_chain?.[0]?.prompt || e.id}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          <StatusPill tone={why.tone}>{why.label}</StatusPill>
                          <Typography variant="caption" color="text.secondary">
                            {formOf(e)} · {day(e.reports[0]?.created_at ?? e.disabled_at ?? e.created_at)}
                          </Typography>
                        </Stack>
                      }
                      slotProps={{ secondary: { component: "div" } }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Card>

          {selected && (
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h3" dir="auto">
                        {selected.facts.objective_title || "Quête"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formOf(selected)}
                        {selected.kind === "body"
                          ? ` · partagée, servie ${selected.uses ?? 0} fois`
                          : " · non partagée (aucun autre élève ne la reçoit)"}
                      </Typography>
                    </Box>
                    {selected.kind === "body" ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button color="error" disabled={busy} onClick={() => void reject(selected)}>
                          Rejeter
                        </Button>
                        <Button variant="outlined" disabled={busy} onClick={() => void validate(selected)}>
                          Valider
                        </Button>
                        <Button
                          variant="contained"
                          disabled={busy || !selected.body}
                          onClick={() => {
                            setEditError(null);
                            setEditing(true);
                          }}
                        >
                          Corriger
                        </Button>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="outlined" disabled={busy} onClick={() => void close(selected, "dismissed")}>
                          Elle était juste
                        </Button>
                        <Button variant="contained" color="error" disabled={busy} onClick={() => void close(selected, "fixed")}>
                          Elle était fausse
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                  <QuestView body={selected.body} />
                </CardContent>
              </Card>

              {(selected.reports.length > 0 || selected.verdicts.length > 0) && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      Pourquoi elle est ici
                    </Typography>
                    <Stack spacing={1}>
                      {selected.verdicts.map((v, i) => (
                        <Box key={`v${i}`}>
                          <Typography variant="body2">
                            <b>{VERDICTS[v.type] ?? v.type}</b>
                            {v.model ? ` (${v.model})` : ""} {day(v.at)}
                          </Typography>
                          {v.reason && (
                            <Typography variant="body2" color="text.secondary" dir="auto" sx={{ whiteSpace: "pre-wrap" }}>
                              {v.reason}
                            </Typography>
                          )}
                        </Box>
                      ))}
                      {selected.reports.map((r) => (
                        <Box key={r.id}>
                          <Typography variant="body2">
                            <b>{REASONS[r.reason] ?? r.reason}</b> — élève {r.reporter_id}, {day(r.created_at)}
                          </Typography>
                          {r.comment && (
                            <Typography variant="body2" color="text.secondary" dir="auto">
                              « {r.comment} »
                            </Typography>
                          )}
                          {r.answers.map((a) => (
                            <Typography key={a.step_id} variant="caption" color="text.secondary" component="div">
                              Étape {a.step_id} : a répondu <span className="mono">{a.given ?? "—"}</span>, clé{" "}
                              <span className="mono">{a.expected ?? "—"}</span>
                              {a.correct === true ? " (compté juste)" : a.correct === false ? " (compté faux)" : ""}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    D'où elle vient
                  </Typography>
                  <FactChain objectiveId={selected.objective_id} documentId={selected.document_id} facts={selected.facts} />
                </CardContent>
              </Card>

              {selected.kind === "body" && selected.body && (
                <QuestEditor
                  open={editing}
                  body={selected.body}
                  saving={busy}
                  error={editError}
                  onCancel={() => setEditing(false)}
                  onSubmit={(body) => void correct(selected, body)}
                />
              )}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default QuestReviewPage;
